import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Autocomplete,
  Box,
  Chip,
  Drawer,
  FormHelperText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import DeleteConfirmationDialog from 'components/DeleteConfirmationDialog';
import { Header } from 'components/GlobalStyles/styles';
import LabelledTextField from 'components/LabelledTextField';
import { PayrollCategoryTypes } from 'types/WorkLogTypes';
import { AddEditEmployeeSchema, EmployeeFormValues } from 'utils/schema';

import { FormSection } from '../../ProjectManagement/styles';

// Generic option shape so the drawer stays decoupled from the API response types.
export interface DrawerOption {
  id: string;
  label: string;
}

interface EmployeeDrawerProps {
  initialValues: EmployeeFormValues | null;
  open: boolean;
  isEditing?: boolean;
  teamOptions?: DrawerOption[];
  activityOptions?: DrawerOption[];
  entitlementOptions?: DrawerOption[];
  payrollOptions?: PayrollCategoryTypes[];
  onClose: () => void;
  // Return true (or a promise resolving to true) when the save succeeded; the drawer stays open otherwise.
  onSubmit: (values: EmployeeFormValues) => boolean | Promise<boolean>;
}

const USER_ROLES: DrawerOption[] = [
  { id: 'E', label: 'Employee' },
  { id: 'M', label: 'Manager' },
  { id: 'A', label: 'Admin' },
  { id: 'S', label: 'Supervisor' },
];

const EMPLOYMENT_STATUS: DrawerOption[] = [
  { id: 'FullTime', label: 'Full Time' },
  { id: 'Casual', label: 'Casual' },
  { id: 'PartTime', label: 'Part Time' },
];

const EMPLOYMENT_CATEGORY: DrawerOption[] = [
  { id: 'Permanent', label: 'Permanent' },
  { id: 'Temporary', label: 'Temporary' },
];

const defaultEmployeeValue: EmployeeFormValues = {
  employee_id: '',
  firstname: '',
  lastname: '',
  username: '',
  email: '',
  user_role: 'E',
  is_active: true,
  team_id: null,
  activity_ids: [],
  employment_status: 'FullTime',
  employment_category: 'Permanent',
  address: {
    street: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    phone_number: '',
    fax: '',
    email: '',
    website: '',
    contact_name: '',
  },
};

const EmployeeDrawer: React.FC<EmployeeDrawerProps> = ({
  initialValues,
  open,
  isEditing,
  teamOptions = [],
  activityOptions = [],
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();

  const [showDiscardConfirm, setShowDiscardConfirm] = React.useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<EmployeeFormValues>({
    defaultValues: initialValues ?? defaultEmployeeValue,
    resolver: zodResolver(AddEditEmployeeSchema),
  });

  React.useEffect(() => {
    if (open) {
      reset(initialValues ?? defaultEmployeeValue);
    }
  }, [open, initialValues, reset]);

  const closeDrawer = () => {
    // Move focus out of the drawer before it gets aria-hidden on close,
    // otherwise the browser warns about hiding a focused element.
    (document.activeElement as HTMLElement | null)?.blur();
    reset(initialValues ?? defaultEmployeeValue);
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    closeDrawer();
  };

  const submitForm = async (data: EmployeeFormValues) => {
    const succeeded = await onSubmit(data);
    if (succeeded) closeDrawer();
  };

  const toggleButtonStyles = {
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: theme.palette.busmanColors.busmanPrimary,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.busmanColors.busmanPrimary,
      },
    },
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          role="presentation"
          sx={{ borderRadius: 2, width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', p: { xs: 2, sm: 3 } }}>
          <Header>
            <Typography variant="h4">{isEditing ? 'Edit Employee' : 'New Employee'}</Typography>
            <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>

          <FormSection component="form" onSubmit={handleSubmit(submitForm)}>
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <LabelledTextField
                dataTestId="employee-input-firstname"
                name="firstname"
                label=" FIRST NAME"
                control={control}
              />
              <LabelledTextField
                dataTestId="employee-input-lastname"
                name="lastname"
                label=" LAST NAME"
                control={control}
              />
            </Stack>

            <Stack direction="row">
              <LabelledTextField
                dataTestId="employee-input-username"
                name="username"
                label=" USERNAME"
                control={control}
              />
            </Stack>

            <Stack direction="row">
              <LabelledTextField
                dataTestId="employee-input-email"
                name="email"
                label=" EMAIL"
                type="email"
                control={control}
              />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <Controller
                name="user_role"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Stack spacing={0.5} sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      ROLE
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={USER_ROLES}
                      value={USER_ROLES.find((role) => role.id === field.value) ?? null}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      onChange={(_event, value) => field.onChange(value?.id ?? '')}
                      ListboxProps={{ style: { fontSize: '12px' } }}
                      data-testid="employee-role-select"
                      popupIcon={<ExpandMoreIcon fontSize="small" />}
                      renderInput={(params) => <TextField {...params} error={!!error} />}
                    />
                    {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                  </Stack>
                )}
              />

              <Stack spacing={0.5} sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                  STATUS
                </Typography>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={field.value ?? true}
                      onChange={(_event, value) => {
                        if (value !== null) field.onChange(value);
                      }}
                      sx={toggleButtonStyles}>
                      <ToggleButton value={true} data-testid="employee-toggle-active">
                        Active
                      </ToggleButton>
                      <ToggleButton value={false} data-testid="employee-toggle-inactive">
                        Inactive
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Stack>
            </Stack>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                EMPLOYMENT
              </Typography>

              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Controller
                  name="employment_status"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                      <Typography variant="body2">STATUS</Typography>
                      <Autocomplete
                        size="small"
                        options={EMPLOYMENT_STATUS}
                        value={EMPLOYMENT_STATUS.find((option) => option.id === field.value) ?? null}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={(_event, value) => field.onChange(value?.id ?? '')}
                        ListboxProps={{ style: { fontSize: '12px' } }}
                        data-testid="employee-employment-status-select"
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        renderInput={(params) => <TextField {...params} error={!!error} />}
                      />
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </Stack>
                  )}
                />

                <Controller
                  name="employment_category"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                      <Typography variant="body2">CATEGORY</Typography>
                      <Autocomplete
                        size="small"
                        options={EMPLOYMENT_CATEGORY}
                        value={EMPLOYMENT_CATEGORY.find((option) => option.id === field.value) ?? null}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={(_event, value) => field.onChange(value?.id ?? '')}
                        ListboxProps={{ style: { fontSize: '12px' } }}
                        data-testid="employee-employment-category-select"
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        renderInput={(params) => <TextField {...params} error={!!error} />}
                      />
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </Stack>
                  )}
                />
              </Stack>

              <Stack direction="row">
                <Controller
                  name="team_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                      <Typography variant="body2">TEAM</Typography>
                      <Autocomplete
                        size="small"
                        options={teamOptions}
                        value={teamOptions.find((option) => option.id === field.value) ?? null}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={(_event, value) => field.onChange(value?.id ?? null)}
                        ListboxProps={{ style: { fontSize: '12px' } }}
                        data-testid="employee-team-select"
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        renderInput={(params) => <TextField {...params} error={!!error} />}
                      />
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </Stack>
                  )}
                />
              </Stack>

              <Stack direction="row">
                <Controller
                  name="activity_ids"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                      <Typography variant="body2">ALLOWABLE ACTIVITIES</Typography>
                      <Autocomplete
                        multiple
                        size="small"
                        options={activityOptions}
                        value={activityOptions.filter((option) => (field.value ?? []).includes(option.id))}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={(_event, value) => field.onChange(value.map((option) => option.id))}
                        ListboxProps={{ style: { fontSize: '12px' } }}
                        data-testid="employee-activities-select"
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return <Chip key={key} size="small" label={option.label} {...tagProps} />;
                          })
                        }
                        renderInput={(params) => <TextField {...params} error={!!error} />}
                      />
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </Stack>
                  )}
                />
              </Stack>

              <Stack spacing={2} sx={{ width: '100%' }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                  ADDRESS
                </Typography>

                <Stack direction="row">
                  <LabelledTextField
                    dataTestId="employee-contact-input"
                    name="address.contact_name"
                    label="CONTACT NAME"
                    control={control}
                  />
                </Stack>

                <Stack direction="row">
                  <LabelledTextField
                    dataTestId="address-email-input"
                    name="address.email"
                    label="EMAIL"
                    type="email"
                    control={control}
                  />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <LabelledTextField
                    dataTestId="address-phone-input"
                    name="address.phone_number"
                    label=" PHONE NUMBER"
                    control={control}
                  />
                  <LabelledTextField dataTestId="address-fax-input" name="address.fax" label=" FAX" control={control} />
                </Stack>

                <Stack direction="row">
                  <LabelledTextField
                    dataTestId="address-street-input"
                    name="address.street"
                    label=" STREET"
                    control={control}
                  />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <LabelledTextField
                    dataTestId="address-city-input"
                    name="address.city"
                    label=" CITY"
                    control={control}
                  />
                  <LabelledTextField
                    dataTestId="address-state-input"
                    name="address.state"
                    label=" STATE"
                    control={control}
                  />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <LabelledTextField
                    dataTestId="address-postal-code-input"
                    name="address.postal_code"
                    label=" POSTAL CODE"
                    control={control}
                  />
                  <LabelledTextField
                    dataTestId="address-country-input"
                    name="address.country"
                    label=" COUNTRY"
                    control={control}
                  />
                </Stack>

                <Stack direction="row">
                  <LabelledTextField
                    dataTestId="address-website-input"
                    name="address.website"
                    label=" WEBSITE"
                    control={control}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ width: '100%' }}>
              <CustomButton
                testid="employee-cancel-button"
                variant="contained_grey"
                width="medium"
                text="Cancel"
                onClick={handleClose}
              />
              <CustomButton
                testid="employee-submit-button"
                type="submit"
                variant="contained"
                width="medium"
                text={isEditing ? 'Update' : 'Save'}
              />
            </Stack>
          </FormSection>
        </Box>
      </Drawer>

      {showDiscardConfirm && (
        <DeleteConfirmationDialog
          title="Discard changes?"
          description="You have unsaved changes. Are you sure you want to close without saving?"
          buttonText="Discard"
          onClose={() => setShowDiscardConfirm(false)}
          onDelete={closeDrawer}
        />
      )}
    </>
  );
};

export default EmployeeDrawer;
